import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, formatDistance } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number | string, currency: string | object = "USD"): string {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  // Ensure currency is a string
  const currencyCode = typeof currency === "string" ? currency : "USD";
  
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
}

export function formatDate(date: Date | string, formatString = "MMM d, yyyy"): string {
  try {
    // Handle timezone issues by adding timezone offset to preserve the intended date
    if (typeof date === 'string') {
      // If it's a date string, parse it as local date to avoid timezone shifts
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return 'Invalid Date';
      }
      // Add timezone offset to counteract the UTC conversion
      const offsetDate = new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);
      return format(offsetDate, formatString);
    }
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    return format(dateObj, formatString);
  } catch (error) {
    return 'Invalid Date';
  }
}

export function formatDateDistance(date: Date | string): string {
  return formatDistance(new Date(date), new Date(), { addSuffix: true });
}

export function getInitials(name: string): string {
  if (!name) return "";

  const parts = name.split(" ");

  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Block null/undefined and the LITERAL string "undefined" from leaking into
 * rendered text via concatenation. Use everywhere a business field is joined
 * into a template string.
 */
export function safe(value?: string | null): string {
  if (value == null) return "";
  if (value === "undefined" || value === "null") return "";
  return value;
}

export function getStatusColor(status: string): {
  bg: string;
  text: string;
} {
  if (!status) return { bg: "bg-gray-100", text: "text-gray-800" };
  switch (status.toLowerCase()) {
    case "active":
    case "completed":
    case "categorized":
      return { bg: "bg-green-100", text: "text-green-800" };
    case "pending":
    case "upcoming":
      return { bg: "bg-blue-100", text: "text-blue-800" };
    case "overdue":
    case "urgent":
      return { bg: "bg-red-100", text: "text-red-800" };
    case "canceled":
    case "needs_review":
    case "due_soon":
      return { bg: "bg-yellow-100", text: "text-yellow-800" };
    default:
      return { bg: "bg-gray-100", text: "text-gray-800" };
  }
}

export function getTransactionTypeColor(type: string): {
  text: string;
} {
  switch (type.toLowerCase()) {
    case "income":
      return { text: "text-success-500" };
    case "expense":
      return { text: "text-destructive" };
    default:
      return { text: "text-gray-500" };
  }
}

export function getCategoryColor(category: string | undefined): string {
  const colors = [
    "bg-blue-100 text-blue-800",
    "bg-green-100 text-green-800",
    "bg-purple-100 text-purple-800",
    "bg-yellow-100 text-yellow-800",
    "bg-red-100 text-red-800",
    "bg-indigo-100 text-indigo-800",
    "bg-pink-100 text-pink-800",
    "bg-teal-100 text-teal-800"
  ];
  
  if (!category) return "bg-gray-100 text-gray-800";
  
  // Create a deterministic index based on the category name
  const index = category.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  
  return colors[index];
}

export function truncateText(text: string, maxLength = 40): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + "...";
}

export function parseCSVFile(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const csv = event.target?.result as string;
        const lines = csv.split("\n");
        const headers = lines[0].split(",").map(header => header.trim());
        
        const result = [];
        
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const obj: Record<string, string> = {};
          const currentLine = lines[i].split(",");
          
          for (let j = 0; j < headers.length; j++) {
            obj[headers[j]] = currentLine[j]?.trim() || "";
          }
          
          result.push(obj);
        }
        
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsText(file);
  });
}

export function getChartColors() {
  return [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))"
  ];
}
